import { useParams } from 'react-router-dom';
import { useT } from '../../i18n';
import { L } from '../tokens';
import { useViewportKind } from '../useViewport';
import { Card, ListRow, Message, Notes, PeriodPills, ReportPage } from './ReportParts';
import { periodBody } from './reportsApi';
import { countText, customerName, supplierName } from './reportFormat';
import { useReportPeriod } from './periodStore';
import { useReport } from './useReport';
import { DocumentsCard, MoneyPair, ProblemsCard, RankingTable } from './WhoParts';

const KINDS = ['this_week', 'this_month', 'this_year', 'custom'];

// The Cases card of a customer: counts only (the Cases lists cannot be filtered by customer yet). Its own request, like the supplier page.
function CasesCard({ id }) {
  const t = useT();
  const { status, data, retry } = useReport('customer-cases', { id });
  return (
    <Card label={t('reports.supplier.cases')} testid="customer-cases">
      {status === 'loading' && <span aria-busy="true" style={{ color: L.muted }}>{t('reports.loading')}</span>}
      {status === 'error' && <button type="button" onClick={retry} style={{ font: 'inherit', cursor: 'pointer' }}>{t('reports.error')} {t('reports.retry')}</button>}
      {status === 'ready' && ['attention', 'open', 'matched'].map((k) => <ListRow key={k} testid={`customer-cases-${k}`} label={t(`reports.cases.${k}`)} value={countText(data.cases[k])} />)}
    </Card>
  );
}

// Leo's "Customer page" (updated board): Orders and Invoiced, Documents per type, the discrepancy types found with Documents late and Price increases apart, the suppliers they buy
// from ranked by order value, and the Cases card. Overdue, Order missing and Reorder late are gone.
export default function CustomerView() {
  const t = useT();
  const { id } = useParams();
  const phone = useViewportKind() === 'phone';
  const [period, setPeriod] = useReportPeriod('past');
  const { status, data, retry } = useReport('customer', { id, ...periodBody(period) });
  const title = data && data.customer ? customerName(data.customer, t) : t('reports.customers.title');
  const two = { display: 'grid', gridTemplateColumns: phone ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: 18 };
  return (
    <ReportPage title={title} backTo="/reports/customers" backLabel={t('reports.customers.title')} pills={<PeriodPills kinds={KINDS} value={period} onChange={setPeriod} />} testid="report-customer">
      {status !== 'ready' ? <Message status={status} onRetry={retry} /> : (
        <>
          <MoneyPair data={data} prefix="cp" phone={phone} />
          <div style={two}>
            <DocumentsCard documents={data.documents} prefix="cp" />
            <ProblemsCard problems={data.problems} currency={data.currency} prefix="cp" />
          </div>
          <RankingTable title={t('reports.customer.suppliers')} testid="customer-suppliers" currency={data.currency}
            columns={[{ key: 'orders', label: t('reports.who.col.orders') }, { key: 'order_value', label: t('reports.who.col.orderValue'), money: true }, { key: 'invoiced', label: t('reports.who.col.invoiced'), money: true, strong: true }]}
            rows={data.suppliers.map((s, i) => ({ id: s.supplier ? s.supplier.id : `none-${i}`, testid: s.supplier ? `cp-supplier-${s.supplier.id}` : 'cp-supplier-none', name: supplierName(s.supplier, t), to: s.supplier && !s.supplier.unknown ? `/reports/suppliers/${s.supplier.id}` : null, bar: s.bar, orders: s.orders, order_value: s.order_value, invoiced: s.invoiced }))} />
          <CasesCard id={id} />
          <Notes notes={data.notes} />
        </>
      )}
    </ReportPage>
  );
}
